<template>
	<img src="../assets/images/header.png" alt="Background Image" class="header-image" />

	<div class="container">
		<!-- Introduction -->
		<h1>ANGELA <br v-if="isMobile"> & <br v-if="isMobile"> JAMES</h1>

		<!-- Countdown -->
        <CountdownTimer />

        <!-- Navigation - Desktop Only -->
        <NavigationMenu @nav-link-click="scrollToSection" />

		<img src="../assets/images/invitation_image_2.jpeg" alt="Invitation Image" class="invitation-image" />

		<!-- Invitation -->
		<div ref="invitationSection" class="section-container">
			<InvitationSection />
		</div>

		<div ref="separator1" class="section-container">
			<DecorativeSeparator />
		</div>

		<!-- RSVP -->
		<div ref="rsvpSection" class="section-container slide-right" :class="{ 'slide-in': visibleSections.rsvp }">
			<RsvpSection
				:family="family"
				:guests="guests"
				:family-not-found="familyNotFound"
				@refresh-data="fetchFamilyData"
			/>
		</div>

		<div ref="separator2" class="section-container slide-right" :class="{ 'slide-in': visibleSections.rsvp }">
			<DecorativeSeparator />
		</div>

		<!-- Welcome Party -->
		<div ref="welcomePartySection" class="section-container slide-right" :class="{ 'slide-in': visibleSections.welcomeParty }">
			<WelcomeParty />
		</div>

		<div ref="separator3" class="section-container slide-left" :class="{ 'slide-in': visibleSections.story }">
			<DecorativeSeparator />
		</div>

		<!-- Our Story -->
		<div ref="storySection" class="section-container slide-left" :class="{ 'slide-in': visibleSections.story }">
			<OurStorySection />
		</div>

		<div ref="separator3" class="section-container slide-left" :class="{ 'slide-in': visibleSections.story }">
			<DecorativeSeparator />
		</div>

		<!-- Accommodation -->
		<div ref="accommodationSection" class="section-container slide-right" :class="{ 'slide-in': visibleSections.accommodation }">
			<AccommodationSection :family="family" />
		</div>

		<div ref="separator4" class="section-container slide-right" :class="{ 'slide-in': visibleSections.accommodation }">
			<DecorativeSeparator />
		</div>

		<!-- Gifts -->
		<div ref="giftsSection" class="section-container slide-left" :class="{ 'slide-in': visibleSections.gifts }">
			<GiftsSection />
		</div>

		<div ref="separator5" class="section-container slide-left" :class="{ 'slide-in': visibleSections.gifts }">
			<DecorativeSeparator />
		</div>

		<!-- FAQ -->
		<div ref="faqSection" class="section-container slide-right" :class="{ 'slide-in': visibleSections.faq }">
			<FaqSection @nav-link-click="scrollToSection" />
		</div>
	</div>

	<img src="../assets/images/header.png" alt="Background Image" class="header-image bottom" />
</template>

<script>
import AccommodationSection from "@/components/AccommodationSection.vue";
import CountdownTimer from "@/components/CountdownTimer.vue";
import DecorativeSeparator from "@/components/DecorativeSeparator.vue";
import FaqSection from "@/components/FaqSection.vue";
import GiftsSection from "@/components/GiftsSection.vue";
import InvitationSection from "@/components/InvitationSection.vue";
import NavigationMenu from "@/components/NavigationMenu.vue";
import OurStorySection from "@/components/OurStorySection.vue";
import RsvpSection from "@/components/RsvpSection.vue";
import WelcomeParty from "@/components/WelcomeParty.vue";
import { supabase } from '@/lib/supabaseClient';
import { useIsMobile } from '@/lib/useIsMobile';
import { useRoute, useRouter } from 'vue-router';

export default {
	name: "HomeView",

	components: {
		CountdownTimer,
		NavigationMenu,
		InvitationSection,
		RsvpSection,
		OurStorySection,
		AccommodationSection,
		GiftsSection,
		FaqSection,
		DecorativeSeparator,
		WelcomeParty,
	},

	data() {
		return {
			guests: [],
			family: null,
			familyNotFound: false,
			visibleSections: {
				invitation: false,
				rsvp: false,
				welcomeParty: false,
				story: false,
				accommodation: false,
				gifts: false,
				faq: false
			}
		}
	},

    setup() {
        const { isMobile } = useIsMobile()
        const router = useRouter()
        return { isMobile, router };
    },

	mounted() {
		const route = useRoute()

		// Check if ID parameter is present
		if (!route.query.id) {
			// Redirect to 404 page if no ID parameter
			this.router.push('/404')
			return
		}

		this.fetchFamilyData(route.query.id)
		this.setupIntersectionObserver()
	},

    methods: {
        setupIntersectionObserver() {
            const options = {
                root: null,
                rootMargin: '0px',
                threshold: 0.1
            };

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const sectionName = this.getSectionName(entry.target);
                        if (sectionName && !this.visibleSections[sectionName]) {
                            this.visibleSections[sectionName] = true;
                        }
                    }
                });
            }, options);

            // Observe all section containers and separators
            this.$nextTick(() => {
                const sections = [
                    'invitationSection',
                    'rsvpSection',
                    'welcomePartySection',
                    'storySection',
                    'accommodationSection',
                    'giftsSection',
                    'faqSection'
                ];

                sections.forEach(sectionRef => {
                    if (this.$refs[sectionRef]) {
                        observer.observe(this.$refs[sectionRef]);
                    }
                });
            });
        },

        getSectionName(element) {
            const refMap = {
                'invitationSection': 'invitation',
                'rsvpSection': 'rsvp',
                'welcomePartySection': 'welcomeParty',
                'storySection': 'story',
                'accommodationSection': 'accommodation',
                'giftsSection': 'gifts',
                'faqSection': 'faq'
            };

            for (const [ref, name] of Object.entries(refMap)) {
                if (this.$refs[ref] === element) {
                    return name;
                }
            }
            return null;
        },

        scrollToSection(sectionId) {
            const el = document.getElementById(sectionId)
            if (el && typeof el.scrollIntoView === 'function') {
                // Temporarily disable animations during programmatic scroll
                this.disableAnimationsTemporarily()
                el.scrollIntoView({ behavior: 'smooth' })
            }
        },

        disableAnimationsTemporarily() {
            // Add a class to temporarily disable animations
            document.body.classList.add('disable-animations')

            // Re-enable animations after scroll completes
            setTimeout(() => {
                document.body.classList.remove('disable-animations')
            }, 1000) // 1 second delay to allow scroll to complete
        },

		async fetchFamilyData(familyId) {
			if (!familyId) {
                this.familyNotFound = true;
                return
            }

			console.log('Fetching family with ID:', familyId);

			// First, check if the family exists and get their RSVP status
			const { data: familyData, error: familyError } = await supabase
				.from("families")
				.select("*")
				.eq("id", familyId)
				.single()

			console.log('Family query result:', { familyData, familyError });

			if (familyError || !familyData) {
				console.error("Family not found:", familyId);
				console.error("Error details:", familyError);
				this.familyNotFound = true;
				return;
			}

			// Store family data
			this.family = familyData;

			// Family exists, now fetch guests
			const { data: guestsData, error: guestsError } = await supabase
				.from("guests")
				.select("*")
				.eq("family_id", familyId)

			if (guestsError) {
				console.error("Failed to fetch guests from Supabase:", guestsError);
				return;
			}

			this.guests = guestsData || []
		},
    }
}
</script>

<style scoped>
.header-image {
    display: block;
    width: 100%;
    max-width: 1449px;
    height: 184px;
    object-fit: cover;
    object-position: center;
    margin: 0 auto;
}

.invitation-image {
    width: 100%;
	max-width: 800px;
    height: auto;
}

.bottom {
    transform: rotate(180deg);
}

.section-container {
    opacity: 0;
    transition: all 0.8s ease-out;
}

/* Invitation section appears immediately */
.section-container:first-of-type,
.section-container:nth-of-type(2) {
    opacity: 1;
    transform: translateX(0);
}

/* Left sliding sections */
.section-container.slide-left {
    transform: translateX(-100px);
}

.section-container.slide-left.slide-in {
    opacity: 1;
    transform: translateX(0);
}

/* Right sliding sections */
.section-container.slide-right {
    transform: translateX(100px);
}

.section-container.slide-right.slide-in {
    opacity: 1;
    transform: translateX(0);
}

/* Disable animations during programmatic scroll */
.disable-animations .section-container {
    transition: none !important;
    opacity: 1 !important;
    transform: translateX(0) !important;
}
</style>